import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject, Observable, ReplaySubject } from "rxjs";
import { BlacklistProfile } from "app/features/nconnect-voice/interfaces/blacklist-profile/blacklist-profile.interface";
import { Trunkset } from "app/features/nconnect-voice/interfaces/trunkset/trunkset.interface";
import { LoadbalancingStrategyPipe } from "app/features/nconnect-voice/pipes/loadbalancing-strategy/loadbalancing-strategy.pipe";
import {
  Numberblock,
  NumberblockEntry,
} from "app/features/nconnect-voice/interfaces/numberblock/numberblock.interface";
import { TableItemTypePipe } from "app/features/nconnect-voice/pipes/table-item-type/table-item-type.pipe";
import { BlacklistDirectionPipe } from "app/features/nconnect-voice/pipes/blacklist-direction/blacklist-direction.pipe";

@Injectable({
  providedIn: "root",
})
export class NconnectItemProviderService {
  private nconnectItemNameSource: BehaviorSubject<string> =
    new BehaviorSubject<string>("");
  private nconnectItemSource: ReplaySubject<BlacklistProfile> =
    new ReplaySubject<BlacklistProfile>(1);
  // If a user used the navigation to go to a detail view, this value will become true
  public nconnectItemNameAvailableFromNavigation: boolean = false;

  public nconnectItemName$: Observable<string> =
    this.nconnectItemNameSource.asObservable();
  public nconnectItem$: Observable<BlacklistProfile> =
    this.nconnectItemSource.asObservable();

  constructor(
    private router: Router,
    private loadbalancingStrategyPipe: LoadbalancingStrategyPipe,
    private tableItemTypePipe: TableItemTypePipe,
    private blacklistDirectionPipe: BlacklistDirectionPipe,
  ) {}

  public setNconnectItem(nconnectItem: BlacklistProfile): void {
    this.nconnectItemSource.next(nconnectItem);
    this.setNconnectItemName(nconnectItem);
  }

  /**
   * Saves the passed nconnect item temporarily when navigating through the page
   * It's needed to update the page title and the sidenav item name
   */
  public setNconnectItemName(
    nconnectItem: BlacklistProfile | NumberblockEntry | Trunkset | Numberblock,
  ): void {
    let itemName: string;

    if (this.router.url.includes("blacklist")) {
      itemName = this.getBlacklistEntryName(nconnectItem as BlacklistProfile);
      this.emitItemName(itemName);
    } else if (this.router.url.includes("pbx-endpoint")) {
      itemName = this.getTrunksetPageTitle(nconnectItem as Trunkset);
      this.emitItemName(itemName);
    } else if (this.router.url.includes("numberblock")) {
      itemName = this.getNumberblockEntryName(nconnectItem as Numberblock);
      this.emitItemName(itemName);
    }
  }

  private emitItemName(itemName: string): void {
    this.nconnectItemNameAvailableFromNavigation = true;
    this.nconnectItemNameSource.next(itemName);
  }

  private getTrunksetPageTitle(trunkset: Trunkset): string {
    return `${trunkset.NAME} (${this.getTrunksetStrategyName(trunkset)})`;
  }

  private getTrunksetStrategyName(trunkset: Trunkset): string {
    return this.loadbalancingStrategyPipe.transform(trunkset.STRATEGY);
  }

  private getBlacklistEntryName(blacklistProfile: BlacklistProfile): string {
    const direction: string = this.blacklistDirectionPipe.transform(
      blacklistProfile.DIRECTION,
    );
    const type: string = this.tableItemTypePipe.transform(
      blacklistProfile.CUSTOMER_ID,
    );
    return `${blacklistProfile.NAME} (${direction}, ${type})`;
  }

  private getNumberblockEntryName(numberblock: Numberblock): string {
    const numberblockEntryName: string = `+${numberblock.COUNTRY_CODE} ${numberblock.NDC} ${numberblock.PILOT}`;

    if (numberblock.NUMBERBLOCK_END) {
      return `${numberblockEntryName} (${numberblock.NUMBERBLOCK_START}-${numberblock.NUMBERBLOCK_END})`;
    }

    return numberblockEntryName;
  }
}
